import React, { useState, useEffect, useRef } from 'react';
import {
  History,
  Monitor,
  Smartphone,
  ArrowLeft,
  Download,
  ExternalLink,
  Loader2,
  VideoOff,
  Layers,
  CheckSquare,
  Square,
  X,
  ArrowUp,
  ArrowDown,
  Trash2,
  CheckCircle2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from './services/api';
import { useTranslation } from './LanguageContext';

function Projects({ onNavigate }) {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [orientations, setOrientations] = useState({});
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState(0);
  const [activePrompt, setActivePrompt] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await api.get('/video/history');
        setVideos(response.data.history || []);
      } catch (err) {
        console.error("Erreur lors du chargement de l'historique", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const handleMetadata = (id, e) => {
    const { videoWidth, videoHeight } = e.target;
    const orientation = videoWidth < videoHeight ? 'portrait' : 'landscape';
    setOrientations(prev => ({ ...prev, [id]: orientation }));
  };

  const handleViewPrompt = (prompt, e) => {
    e.stopPropagation();
    setActivePrompt(prompt);
  };

  const toggleSelectionMode = () => {
    if (!isSelectionMode) {
      setShowInstructionModal(true);
    }
    setIsSelectionMode(!isSelectionMode);
    setSelectedVideos([]);
  };

  const handleVideoSelect = (video) => {
    if (selectedVideos.find(v => v.id === video.id)) {
      setSelectedVideos(selectedVideos.filter(v => v.id !== video.id));
    } else {
      setSelectedVideos([...selectedVideos, video]);
    }
  };

  const moveVideo = (index, direction) => {
    const newList = [...selectedVideos];
    if (direction === 'up' && index > 0) {
        [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
    } else if (direction === 'down' && index < newList.length - 1) {
        [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    }
    setSelectedVideos(newList);
  };

  const removeVideoFromMerge = (index) => {
    const newList = [...selectedVideos];
    newList.splice(index, 1);
    setSelectedVideos(newList);
  };

  const executeMerge = async () => {
    if (merging || selectedVideos.length < 2) return;
    setMerging(true);
    setMergeProgress(0);

    // Simulation de progression
    const progressInterval = setInterval(() => {
      setMergeProgress(prev => {
        if (prev >= 90) return 90;
        return prev + 5;
      });
    }, 500);

    try {
        const videoUrls = selectedVideos.map(v => v.video_url);
        
        const mergeResponse = await fetch('https://service.ralp-ai.site/merge-videos', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ videos: videoUrls })
        });

        if (!mergeResponse.ok) throw new Error('Erreur fusion service Python');
        const mergedBlob = await mergeResponse.blob();
        
        const formData = new FormData();
        formData.append('file', mergedBlob, 'merged_video.mp4');
        
        await api.post('/video/save-merged', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Rafraîchir l'historique
        const response = await api.get('/video/history');
        setVideos(response.data.history || []);
        
        setShowMergeModal(false);
        clearInterval(progressInterval);
        setIsSelectionMode(false);
        setSelectedVideos([]);
        alert("Fusion terminée ! La vidéo a été ajoutée à vos projets.");

    } catch (err) {
        console.error(err);
        clearInterval(progressInterval);
        alert("Erreur lors de la fusion.");
    } finally {
        setMerging(false);
        setMergeProgress(0);
    }
  };

  const filteredVideos = videos.filter(video => {
    if (filter === 'all') return true;
    const orientation = orientations[video.id];
    if (!orientation) return true;
    return orientation === filter;
  });

  return (
    <div className="dashboard-wrapper">
      <style>{`
        @media (max-width: 768px) {
          .dashboard-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 15px;
            height: auto !important;
          }
          .header-buttons-container {
            width: 100%;
            display: flex !important;
            flex-wrap: wrap;
            gap: 10px !important;
          }
          .header-buttons-container button {
            flex: 1;
            justify-content: center;
            padding: 10px !important;
            font-size: 0.9rem !important;
          }
          .filter-container button {
            flex: 1 0 auto;
            justify-content: center;
          }
        }
      `}</style>
      <div className="dashboard-header reveal">
        <div className="header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <History size={24} color="var(--accent)" />
            <h2 style={{ fontSize: '2rem', margin: 0, color: 'var(--text-main)' }}>{t('myProjects')}</h2>
          </div>
          <p style={{ color: 'var(--text-muted)', margin: '0.5rem 0 0 0' }}>{t('libraryDesc')}</p>
        </div>
        <div className="header-buttons-container" style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-apple-secondary" onClick={() => onNavigate('dashboard')}>
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            {t('dashboard')}
          </button>
        </div>
      </div>

      <div className="filter-container reveal" style={{ marginBottom: '3rem', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          {t('viewAll')}
        </button>
        <button
          className={`filter-btn ${filter === 'landscape' ? 'active' : ''}`}
          onClick={() => setFilter('landscape')}
        >
          <Monitor size={14} style={{ marginRight: '8px' }} />
          {t('landscape')}
        </button>
        <button
          className={`filter-btn ${filter === 'portrait' ? 'active' : ''}`}
          onClick={() => setFilter('portrait')}
        >
          <Smartphone size={14} style={{ marginRight: '8px' }} />
          {t('portrait')}
        </button>
        {!isSelectionMode ? (
          <button className="btn-apple-secondary" onClick={toggleSelectionMode}>
            <Layers size={16} style={{ marginRight: '8px' }} />
            {t('merge')}
          </button>
        ) : (
          <>
            <button 
              className="btn-apple-primary" 
              onClick={() => setShowMergeModal(true)} 
              disabled={selectedVideos.length < 2}
              style={{ background: '#ec4899', borderColor: '#ec4899' }}
            >
              <Layers size={16} style={{ marginRight: '8px' }} />
              {t('merge')} ({selectedVideos.length})
            </button>
            <button className="btn-apple-secondary" onClick={toggleSelectionMode}>
              <X size={16} style={{ marginRight: '8px' }} />
              {t('cancel')}
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className="loading-state reveal">
          <Loader2 size={32} className="animate-spin" color="var(--accent)" />
          <p>{t('loadingLibrary')}</p>
        </div>
      ) : videos.length === 0 ? (
        <div className="empty-state reveal">
          <VideoOff size={48} strokeWidth={1} opacity={0.3} style={{ marginBottom: '1.5rem' }} />
          <p className="empty-title">{t('emptyLibrary')}</p>
          <p className="empty-subtitle">{t('emptyLibraryDesc')}</p>
          <button className="btn-apple-primary" onClick={() => onNavigate('create')} style={{ marginTop: '2rem' }}>
            {t('createFirstVideo')}
          </button>
        </div>
      ) : (
        <div className="projects-grid reveal">
          {filteredVideos.map((video) => {
            const orientation = orientations[video.id] || 'landscape';
            const isSelected = selectedVideos.find(v => v.id === video.id);
            return (
                <div 
                key={video.id} 
                className={`project-card ${orientation} reveal`}
                style={{ 
                  border: isSelected ? '2px solid #ec4899' : '1px solid transparent',
                  transform: isSelected ? 'scale(0.98)' : 'scale(1)',
                  transition: 'all 0.2s ease',
                  cursor: isSelectionMode ? 'pointer' : 'default'
                }}
                onClick={() => isSelectionMode && handleVideoSelect(video)}
              >
                <div className={`video-wrapper ${orientation}`}>
                  {isSelectionMode && (
                    <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>
                      {isSelected ? <CheckSquare color="#ec4899" fill="white" size={24} /> : <Square color="white" fill="rgba(0,0,0,0.5)" size={24} />}
                    </div>
                  )}
                  {isSelectionMode && <div style={{ position: 'absolute', inset: 0, zIndex: 5 }} />}
                  <video
                    controls
                    preload="metadata"
                    src={video.video_url}
                    width="100%"
                    height="100%"
                    onLoadedMetadata={(e) => handleMetadata(video.id, e)}
                  />
                </div>
                <div className="project-info">
                  <p className="project-date">{new Date(video.created_at).toLocaleDateString()}</p>
                  <div style={{ marginBottom: '10px' }}>
                    <p 
                      className="project-prompt" 
                      style={{ 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: '4px'
                      }}
                    >
                      {video.prompt}
                    </p>
                    <button 
                      onClick={(e) => handleViewPrompt(video.prompt, e)}
                      style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '0.8rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {t('viewAllText')} <ChevronDown size={14} />
                    </button>
                  </div>
                  <div className="project-actions" style={{ marginTop: '1.5rem', display: 'flex', gap: '10px' }}>
                    <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="btn-apple-primary sm w-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ExternalLink size={14} style={{ marginRight: '6px' }} />
                      {t('viewDownload')}
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showMergeModal && (
        <div className="modal-overlay reveal">
          <div className="modal-content glass reveal" style={{ maxWidth: '600px' }}>
            <button className="close-btn" onClick={() => setShowMergeModal(false)}><X size={20} /></button>
            <div className="modal-header">
              <h2 className="modal-title">{t('mergeSequences')}</h2>
              <p className="modal-subtitle">{t('organizeOrder')} {selectedVideos.length} {t('videos')}</p>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto', margin: '20px 0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {selectedVideos.map((video, idx) => (
                <div key={video.id} style={{ 
                  display: 'flex', alignItems: 'center', gap: '15px', 
                  background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' 
                }}>
                  <span style={{ fontWeight: 'bold', color: 'var(--text-muted)', width: '20px' }}>{idx + 1}</span>
                  <video src={video.video_url} style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px', background: '#000' }} />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{video.prompt || t('untitled')}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{new Date(video.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button onClick={() => moveVideo(idx, 'up')} disabled={idx === 0} style={{ padding: '5px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '4px', color: 'var(--text-main)', cursor: 'pointer', opacity: idx === 0 ? 0.3 : 1 }}>
                      <ArrowUp size={16} />
                    </button>
                    <button onClick={() => moveVideo(idx, 'down')} disabled={idx === selectedVideos.length - 1} style={{ padding: '5px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '4px', color: 'var(--text-main)', cursor: 'pointer', opacity: idx === selectedVideos.length - 1 ? 0.3 : 1 }}>
                      <ArrowDown size={16} />
                    </button>
                    <button onClick={() => removeVideoFromMerge(idx)} style={{ padding: '5px', background: 'rgba(239, 68, 68, 0.2)', border: 'none', borderRadius: '4px', color: '#ef4444', cursor: 'pointer' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-apple-primary w-full" 
                onClick={executeMerge} 
                disabled={merging || selectedVideos.length < 2}
                style={{ background: '#ec4899', justifyContent: 'center' }}
              >
                {merging ? (
                  <><Loader2 size={18} className="animate-spin" style={{ marginRight: '8px' }} /> {t('merging')} {mergeProgress}%</>
                ) : (
                  <><Layers size={18} style={{ marginRight: '8px' }} /> {t('startMerge')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {activePrompt && (
        <div className="modal-overlay reveal" style={{ zIndex: 2000 }} onClick={() => setActivePrompt(null)}>
          <div className="modal-content glass reveal" style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setActivePrompt(null)}><X size={20} /></button>
            <div className="modal-header">
              <h2 className="modal-title">{t('fullPrompt')}</h2>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', color: 'var(--text-main)', padding: '10px 0' }}>
              {activePrompt}
            </div>
            <div className="modal-footer">
              <button className="btn-apple-primary w-full" onClick={() => setActivePrompt(null)}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}

      {showInstructionModal && (
        <div className="modal-overlay reveal" style={{ zIndex: 2000 }}>
          <div className="modal-content glass reveal" style={{ maxWidth: '400px', textAlign: 'center' }}>
            <div className="modal-header" style={{ justifyContent: 'center', marginBottom: '10px' }}>
              <h2 className="modal-title">{t('mergeMode')}</h2>
            </div>
            <p style={{ marginBottom: '20px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
              {t('mergeInstructions')}
            </p>
            <button 
              className="btn-apple-primary w-full" 
              onClick={() => setShowInstructionModal(false)}
              style={{ justifyContent: 'center' }}
            >
              {t('understood')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;